import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const EmergencyAgentModal = ({ isOpen, onClose, onSave, agentToEdit }) => {
  const isEditMode = !!agentToEdit;
  const [agentData, setAgentData] = useState({
    name: "",
    contactNo: "",
    address: "",
    division: "Dhaka",
    district: "Dhaka",
    upazila: "",
    union: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && agentToEdit) {
        setAgentData({
          name: agentToEdit.name || "",
          contactNo: agentToEdit.contactNo || "",
          address: agentToEdit.address || "",
          division: agentToEdit.division || "Dhaka",
          district: agentToEdit.district || "Dhaka",
          upazila: agentToEdit.upazila || "",
          union: agentToEdit.union || "",
        });
      } else {
        setAgentData({
          name: "",
          contactNo: "",
          address: "",
          division: "Dhaka",
          district: "Dhaka",
          upazila: "",
          union: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, agentToEdit, isEditMode]);

  const validateForm = () => {
    const newErrors = {};
    if (!agentData.name.trim()) newErrors.name = "Agent Name is required";
    if (!agentData.contactNo.trim()) newErrors.contactNo = "Contact Number is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgentData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async () => {
    if (!validateForm()) {
        toast.error("Please fill all required fields.");
        return;
    }

    setIsLoading(true);
    try {
      const payload = {
        agentName: agentData.name,
        contactNumber: agentData.contactNo,
        address: agentData.address,
        division: agentData.division,
        district: agentData.district,
        upazila: agentData.upazila,
        union: agentData.union,
      };

      if (isEditMode) {
        await axios.put(
          `https://api.muktihospital.com/api/emergency/agents/edit/${agentToEdit.id}`,
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        toast.success("Agent updated successfully!");
      } else {
        await axios.post(
          "https://api.muktihospital.com/api/emergency/agents/add",
          payload,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );
        toast.success("Agent added successfully!");
      }
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving agent:", error);
      toast.error(error.response?.data?.error || "Failed to save agent.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[100vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">{isEditMode ? "Edit Agent" : "Add Agent"}</h2>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agent Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={agentData.name}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.name ? "border-red-500" : ""
              }`}
              placeholder="Enter agent name"
              disabled={isLoading}
            />
            {errors.name && (
              <span className="text-red-500 text-xs mt-1">{errors.name}</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="contactNo"
              value={agentData.contactNo}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.contactNo ? "border-red-500" : ""
              }`}
              placeholder="Enter contact number"
              disabled={isLoading}
            />
            {errors.contactNo && (
              <span className="text-red-500 text-xs mt-1">{errors.contactNo}</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={agentData.address}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter address"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Division
            </label>
            <input
              type="text"
              name="division"
              value={agentData.division}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter division"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              District
            </label>
            <input
              type="text"
              name="district"
              value={agentData.district}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter district"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upazila
            </label>
            <input
              type="text"
              name="upazila"
              value={agentData.upazila}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter upazila"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Union
            </label>
            <input
              type="text"
              name="union"
              value={agentData.union}
              onChange={handleChange}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              placeholder="Enter union"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyAgentModal;